import * as React from 'react'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import { Role } from '@user/store/roles/typings'
import {
  activeUserDefault,
  adminRoleName,
  guestRoleName,
  userDisabledCoreProduct,
  userNoAccess,
} from '@user/components/Settings/Users/UpdateUser/constants'
import { ITenantUser } from '@user/store/users'
import { ActionMenu } from '@user/components/Settings/Users/UsersNew/ActionMenu'
import { UserStatusToggle } from '@user/components/Settings/Users/UsersNew/UserStatusToggle'
import { InfoTooltip } from '@sales-center/components'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'

interface Props {
  roles: Role[]
  handleEditClick?: (userId: string, initialActive?: boolean) => void
  handleDeleteRequest?: (userId: string) => void
  disableDeleteTooltip?: (user: ITenantUser) => string | undefined
  updateUserStatus?: (user: ITenantUser, reloadUsers?: boolean) => void
  handleUpdateFromPPRequest?: (externalUserId: string) => void
  licenseLeft?: number
  tidalThemeActive?: boolean
  hasSSOErrors?: boolean
  formatDate?: (
    date: string | number | Date | null | undefined,
    type?: string,
  ) => string
}

export const usersListColumns = ({
  roles,
  handleEditClick,
  handleDeleteRequest,
  disableDeleteTooltip,
  handleUpdateFromPPRequest,
  updateUserStatus,
  tidalThemeActive,
  hasSSOErrors,
  formatDate,
}: Props) => {
  const RoleDisplay = (userRoleId: number | undefined | null) => {
    if (!userRoleId) {
      return ''
    }
    const matchedRole: Role | undefined | null = roles.find(
      (x) => x.id == userRoleId,
    )
    return matchedRole?.name ?? ''
  }

  return [
    {
      width: 50,
      type: 'actions',
      field: 'actions',
      headerName: 'Actions',
      renderHeader: () => null,
      sortable: false,
      resizable: false,
      filterable: false,
      disableReorder: true,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        handleEditClick &&
        handleDeleteRequest &&
        handleUpdateFromPPRequest &&
        disableDeleteTooltip && (
          <ActionMenu
            user={row}
            handleEditClick={handleEditClick}
            handleDeleteRequest={handleDeleteRequest}
            disableDeleteTooltip={disableDeleteTooltip}
            handleUpdateFromPPRequest={handleUpdateFromPPRequest}
          />
        ),
    },
    {
      field: 'status',
      headerName: 'Status',
      renderHeader: () => (
        <Stack alignItems="center" direction="row">
          <Typography
            sx={{ fontWeight: tidalThemeActive ? 'bold' : '' }}
            variant="body2"
          >
            Status&nbsp;
          </Typography>
          <InfoTooltip
            infoText={activeUserDefault}
            tooltipPlacement="right"
            sx={{ fontSize: '18px' }}
            iconColor="action"
          />
        </Stack>
      ),
      sortable: false,
      disableReorder: true,
      minWidth: 145,
      renderCell: ({ row }) => (
        <UserStatusToggle
          user={row}
          updateUserStatus={updateUserStatus}
          handleEditClick={handleEditClick}
        />
      ),
    },
    {
      field: 'errors',
      headerName: 'Errors',
      width: 210,
      sortable: false,
      disableReorder: true,
      hide: !hasSSOErrors,
      valueGetter: ({ row }) =>
        !row.isCoreProductEnabled && row.isActive && row.isSSOMappedUser
          ? userNoAccess
          : '',
      renderCell: ({ row }) =>
        !row.isCoreProductEnabled && row.isActive && row.isSSOMappedUser ? (
          <Stack alignItems="center" direction="row">
            <Typography
              sx={{ color: 'rgba(214, 59, 31, 1)', fontSize: '14px' }}
              variant="body2"
            >
              {userNoAccess}
            </Typography>
            <InfoTooltip
              infoText={userDisabledCoreProduct}
              tooltipPlacement="bottom"
              sx={{ fontSize: '16px' }}
              iconColor="action"
            />
          </Stack>
        ) : (
          <></>
        ),
    },
    {
      field: 'employee',
      headerName: 'Employee',
      valueGetter: ({ row }) => `${row.firstName} ${row.lastName}`,
      renderCell: ({ row }) =>
        row.isCoreProductEnabled ? (
          <Link
            href=""
            onClick={(e) => {
              e.preventDefault()
              if (handleEditClick) handleEditClick(row.id)
            }}
          >
            {`${row.firstName} ${row.lastName}`}
          </Link>
        ) : (
          <span>{`${row.firstName} ${row.lastName}`}</span>
        ),
    },
    {
      field: 'type',
      headerName: 'Employee Type',
      valueGetter: ({ row }) => `${row.isExternalUser ? 'Mapped' : 'Unmapped'}`,
    },
    {
      field: 'userRole',
      headerName: 'Role',
      valueGetter: ({ row }) =>
        row.isSuperAdmin ? 'Super Administrator' : RoleDisplay(row.roleId),
    },
    {
      field: 'branch',
      headerName: 'Branch',
      sortable: false,
      valueGetter: ({ row }) => {
        if (row.roleName == adminRoleName || row.roleName == guestRoleName) {
          return 'All'
        }

        return Array.from(
          row.salesTeams.reduce(
            (acc, user) => acc.add(user.branchName),
            new Set(),
          ),
        ).join(', ')
      },
    },
    {
      field: 'salesTeam',
      headerName: 'Sales Team',
      sortable: false,
      valueGetter: ({ row }) => {
        if (row.roleName == adminRoleName || row.roleName == guestRoleName) {
          return 'All'
        }
        const salesTeamNames = row.salesTeams.map((u) => u.name)
        return salesTeamNames.join(', ')
      },
    },
    {
      field: 'lastLoggedIn',
      headerName: 'Last Login',
      width: 145,
      minWidth: 145,
      valueGetter: ({ row }) =>
        formatDate ? formatDate(row.lastLoggedIn, 'dateTime_12') : '',
    },
  ]
}

export const defaultUsersColVis = usersListColumns({
  roles: [],
}).reduce<GridColumnVisibilityModel>((acc, { field }) => {
  if (acc[field] === undefined) {
    return { ...acc, [field]: true }
  } else return acc
}, {})

export const defaultUsersColumns = usersListColumns({
  roles: [],
}).map((c) => c.field)
